import { adminV2EntryActionSchema } from '@/admin-v2/entry-actions/entry-action';
import { appIconKeys } from '@/components/icons/app-icon-catalog';
import { z } from 'zod';

const siteRuntimeUrlTargetSchema = z.string().refine(
  (value) => {
    if (value.startsWith('/')) return true;
    if (value.startsWith('mailto:')) {
      const [address] = value.slice('mailto:'.length).split('?');
      return z.string().email().safeParse(address).success;
    }
    return z.string().url().safeParse(value).success;
  },
  {
    message:
      'Target URL must be an absolute URL, mailto link, or an internal site path.',
  }
);

export const siteRuntimeTargetSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('none') }).strict(),
  z
    .object({
      type: z.literal('open_url'),
      href: siteRuntimeUrlTargetSchema,
      external: z.boolean(),
    })
    .strict(),
  z
    .object({
      type: z.literal('existing_entry_action'),
      actionId: z.string().min(1),
    })
    .strict(),
  z
    .object({
      type: z.literal('custom_entry_action'),
      action: adminV2EntryActionSchema,
    })
    .strict(),
  z
    .object({
      type: z.literal('client_entry_action'),
      action: adminV2EntryActionSchema,
    })
    .strict(),
]);

export type SiteRuntimeTarget = z.infer<typeof siteRuntimeTargetSchema>;

export const siteRuntimeNavigationLinkBadgeValues = [
  'hot',
  'new',
  '23% off',
  '50% off',
] as const;
export type SiteRuntimeNavigationLinkBadge =
  (typeof siteRuntimeNavigationLinkBadgeValues)[number];

export const siteRuntimeCustomIconKeys = [] as const;
export const siteRuntimeAppIconKeys = appIconKeys;
export type SiteRuntimeIconSource = 'app' | 'custom';

const appIconKeySet = new Set<string>(siteRuntimeAppIconKeys);
const customIconKeySet = new Set<string>(siteRuntimeCustomIconKeys);

export function isSiteRuntimeIconKeyAllowed({
  iconSource,
  iconKey,
}: {
  iconSource: SiteRuntimeIconSource;
  iconKey: string;
}) {
  return iconSource === 'app'
    ? appIconKeySet.has(iconKey)
    : customIconKeySet.has(iconKey);
}

export const siteRuntimeIconSchema = z.discriminatedUnion('iconSource', [
  z
    .object({
      iconSource: z.literal('app'),
      iconKey: z
        .string()
        .refine(
          (iconKey) =>
            isSiteRuntimeIconKeyAllowed({ iconSource: 'app', iconKey }),
          { message: 'App icon key is not whitelisted.' }
        ),
    })
    .strict(),
  z
    .object({
      iconSource: z.literal('custom'),
      iconKey: z
        .string()
        .refine(
          (iconKey) =>
            isSiteRuntimeIconKeyAllowed({ iconSource: 'custom', iconKey }),
          { message: 'Custom icon key is not whitelisted.' }
        ),
    })
    .strict(),
]);

export type SiteRuntimeIcon = z.infer<typeof siteRuntimeIconSchema>;
